import { type Dtos, type FredSyncRunId, FredSyncRunStatus } from "@inrev/common";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useRequest } from "../../../utils/request";
import { boundedPoll } from "../../bounded-poll";
import { fredQueryKey, fredSyncRunQueryKey } from "../fred.keys";

type SyncRun = Dtos.MarketIntelligence.Fred.GetSyncRun.Response;

const POLL_INTERVAL_MS = 3_000;
/** Leaves headroom above a typical full refresh without polling a dead run forever. */
const POLL_MAX_MS = 15 * 60 * 1_000;

/** A stale run has stopped heartbeating and is terminal even if its status remains `running`. */
const isInFlight = (run: SyncRun | undefined): boolean =>
	run !== undefined && run.status === FredSyncRunStatus.Running && !run.isStale;

export const useFetchFredSyncRun = (runId: FredSyncRunId | undefined) => {
	const { get } = useRequest();
	const queryClient = useQueryClient();
	const pollStartedAtMsRef = useRef<number | null>(null);

	useEffect(() => {
		pollStartedAtMsRef.current = null;
	}, [runId]);

	const query = useQuery({
		queryKey: fredSyncRunQueryKey(runId),
		enabled: runId !== undefined,
		queryFn: async () =>
			await get<SyncRun>(`/v2/admin/market-intelligence/fred/sync-runs/${runId}`),
		refetchInterval: (q) =>
			boundedPoll({
				data: q.state.data,
				shouldPoll: isInFlight,
				startedAtMsRef: pollStartedAtMsRef,
				intervalMs: POLL_INTERVAL_MS,
				maxMs: POLL_MAX_MS,
			}),
	});

	/** Snapshot data changes only after the run reaches a terminal status. */
	const settled = query.data !== undefined && !isInFlight(query.data);
	useEffect(() => {
		if (!settled) return;
		void queryClient.invalidateQueries({ queryKey: fredQueryKey() });
	}, [settled, queryClient]);

	return query;
};
